// THE DEVELOPER MARK — two birds in flight, string art in one ink, as the
// fleet's figures are made (Nucleus's lotus, Synapse's hummingbird): straight
// threads at part opacity, a nail at their ends.
//
//   the near bird   two wings, each three strands of nails: a leading edge, a
//                   trailing edge and one long thread across them
//   the far bird    the same bird, smaller, turned up and fainter: the next
//                   one off the ground
//
// Three densities of one figure so it reads at every size: the whole figure
// (splash, dock tile), the seat (the rail, about 34px: both birds, fewer nails) and
// the tab (16px: one pair of wings). Markup is the kit's string-art contract
// (DrawnMark): <line>s and nails in currentColor, their weight and opacity inline.

const f = (v) => v.toFixed(2);
const thread = (a, b, w, o) => `<line x1="${f(a[0])}" y1="${f(a[1])}" x2="${f(b[0])}" y2="${f(b[1])}" style="stroke-width:${w}" opacity="${o}"></line>`;
const nail = (p, r, o) => `<circle cx="${f(p[0])}" cy="${f(p[1])}" r="${r}" fill="currentColor" stroke="none" opacity="${o}"></circle>`;

// One bird, as drawn on a 1024 canvas: each wing is strands of nails, tip first.
const LEFT = [
  [[97, 568], [118, 580], [167, 606], [219, 626], [273, 639], [328, 646], [371, 668]],
  [[118, 580], [395, 718]],
  [[133, 610], [172, 650], [213, 687], [257, 722], [302, 752], [351, 753]],
];
const RIGHT = [
  [[662, 528], [648, 543], [614, 580], [577, 612], [536, 638], [490, 658], [463, 690]],
  [[648, 543], [460, 740]],
  [[645, 575], [624, 620], [601, 663], [576, 705], [546, 745], [506, 757]],
];
const BIRD = [...LEFT, ...RIGHT];

// the far bird: the same strands, scaled down, turned up and set to the upper right
function far(p) {
  const s = 0.54;
  const a = (-14 * Math.PI) / 180;
  const x = (p[0] - 97) * s;
  const y = (p[1] - 568) * s;
  return [600 + x * Math.cos(a) - y * Math.sin(a), 300 + x * Math.sin(a) + y * Math.cos(a)];
}

// Fit strands (in canvas units) into the 200 box, inside `pad`, centred.
function fit(groups, pad) {
  const all = groups.flatMap((g) => g.strands.flat());
  const xs = all.map((p) => p[0]);
  const ys = all.map((p) => p[1]);
  const [x0, x1, y0, y1] = [Math.min(...xs), Math.max(...xs), Math.min(...ys), Math.max(...ys)];
  const k = (200 - 2 * pad) / Math.max(x1 - x0, y1 - y0);
  const ox = 100 - ((x0 + x1) / 2) * k;
  const oy = 100 - ((y0 + y1) / 2) * k;
  return (p) => [ox + p[0] * k, oy + p[1] * k];
}

// Draw groups of strands: threads first (so the splash draws the flight in),
// then a nail on every point, once.
function draw(groups, pad) {
  const at = fit(groups, pad);
  let lines = '';
  let nails = '';
  for (const g of groups) {
    const seen = new Set();
    for (const strand of g.strands) {
      for (let i = 1; i < strand.length; i += 1) lines += thread(at(strand[i - 1]), at(strand[i]), g.w, g.o);
      for (const p of strand) {
        const key = p.join(',');
        if (g.nailR && !seen.has(key)) { seen.add(key); nails += nail(at(p), g.nailR, g.nailO); }
      }
    }
  }
  return lines + nails;
}

const svg = (body) => `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200" width="100%" height="100%" fill="none" stroke="currentColor" stroke-linecap="round">${body}</svg>`;

// the whole figure: the near bird, then the far one behind it
export const DEVELOPER_SVG = svg(draw([
  { strands: BIRD, w: 1.8, o: 0.55, nailR: 4, nailO: 0.9 },
  { strands: BIRD.map((s) => s.map(far)), w: 1.1, o: 0.3, nailR: 2.2, nailO: 0.5 },
], 10));

// the seat: both birds, heavier, nails only where strands meet or end
const ENDS = BIRD.map((s) => (s.length > 3 ? [s[0], s[Math.floor(s.length / 2)], s[s.length - 1]] : s));
export const DEVELOPER_SEAT_SVG = svg(draw([
  { strands: ENDS, w: 8, o: 0.7, nailR: 9.5, nailO: 1 },
  { strands: ENDS.map((s) => s.map(far)), w: 5.5, o: 0.4, nailR: 6, nailO: 0.6 },
], 6));

// the tab: one pair of wings, each a leading and a trailing edge
const WINGS = [LEFT[0], LEFT[2], RIGHT[0], RIGHT[2]].map((s) => [s[0], s[s.length - 1]]);
export const DEVELOPER_TAB_SVG = svg(draw([{ strands: WINGS, w: 22, o: 1, nailR: 0 }], 12));

// the figure for a size
export const developerFigure = (size) => (size >= 64 ? DEVELOPER_SVG : size >= 24 ? DEVELOPER_SEAT_SVG : DEVELOPER_TAB_SVG);
